import type ItinerarySearchForm from '@search/ItinerarySearchForm';
import { TrainSearchForm } from '@search/trip/train/TrainSearchForm';
import { BusSearchForm } from '@search/trip/bus/BusSearchForm';
import { FlightSearchForm } from '@search/trip/flight/FlightSearchForm';
import { HotelSearchForm } from '@search/hotel/HotelSearchForm';
import { BundleSearchForm } from '@search/bundle/BundleSearchForm';
import { ShipSearchForm } from '@search/trip/ship/ShipSearchForm';
import { TripSearchForm } from '@search/trip/TripSearchForm';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import type { PassengerCount } from '@itinerary/types';
import {
    type ItineraryParams,
    type ItineraryPathData,
    ReserveType,
} from '@utils/itinerary';

const ReserveTypes: ReserveType[] = [
    ReserveType.Flight,
    ReserveType.Train,
    ReserveType.Bus,
    ReserveType.Taxi,
    ReserveType.Hotel,
    ReserveType.Ship,
    ReserveType.Bundle,
];
const storeKey = 'search-form';
export const useSearchFormStore = defineStore(storeKey, () => {
    //#region forms
    const flightForm = reactive(new FlightSearchForm()) as FlightSearchForm;
    const hotelForm = reactive(new HotelSearchForm()) as HotelSearchForm;
    const trainForm = reactive(new TrainSearchForm()) as TrainSearchForm;
    const busForm = reactive(new BusSearchForm(null)) as BusSearchForm;
    const taxiForm = reactive(
        new BusSearchForm(8, ReserveType.Taxi)
    ) as BusSearchForm;
    const bundleFlightForm = reactive(
        new FlightSearchForm()
    ) as FlightSearchForm;
    const bundleHotelForm = reactive(new HotelSearchForm()) as HotelSearchForm;
    const bundleForm = reactive(new BundleSearchForm()) as BundleSearchForm;
    const shipForm = reactive(new ShipSearchForm()) as ShipSearchForm;
    //#endregion
    const urlParams = ref<ItineraryParams>({});
    const currentReserveType = ref<ReserveType | null>(null); // The reserveType currently being viewed
    const firstReserveType = ref<ReserveType | null>(null);
    const needClearPackageSets = ref(false);

    const reserveType = computed<ReserveType>(
        () => currentReserveType.value || ReserveType.Flight
    );

    const targetCode = computed(() => {
        switch (reserveType.value) {
            case ReserveType.Hotel: {
                const { userDestination, landingPageStars } = hotelForm;
                if (userDestination) {
                    if (landingPageStars) {
                        return {
                            destination: userDestination.englishTitle || '',
                            hotelName: `${landingPageStars}-star`,
                        };
                    } else if (userDestination.type === 'city') {
                        return {
                            destination: userDestination.englishTitle || '',
                        };
                    } else {
                        return {
                            destination: userDestination.cityTitle || '',
                            hotelName: userDestination.englishTitle,
                        };
                    }
                }
                return { destination: '' };
            }
            default: {
                return {
                    destination:
                        currentSearchForm<TripSearchForm>().destinationCode ||
                        '',
                };
            }
        }
    });

    const getReserveTypeFromPath = (path: string): ReserveType => {
        if (!path.match(/^\/(flight|train|bus|taxi|hotel|tour)/)) {
            return ReserveType.Flight;
        }
        switch (path.match(/^\/(flight|train|bus|taxi|hotel|tour)/)![1]) {
            case 'flight':
                return ReserveType.Flight;
            case 'train':
                return ReserveType.Train;
            case 'bus':
                return ReserveType.Bus;
            case 'taxi':
                return ReserveType.Taxi;
            case 'hotel':
                return ReserveType.Hotel;
            case 'tour':
                return ReserveType.Bundle;
            default:
                throw new Error('reserve type case not handled!');
        }
    };

    const setReserveTypeBaseOnPath = (path: string): void => {
        setReserveType(getReserveTypeFromPath(path));
    };

    const verifyViewPath = (
        path: string,
        pathData: ItineraryPathData
    ): Promise<void> => {
        setReserveTypeBaseOnPath(path);
        return currentSearchForm().verifyViewPath(path, pathData);
    };

    const loadViewPath = async (
        path: string,
        pathData: ItineraryPathData
    ): Promise<ItineraryParams> => {
        setReserveTypeBaseOnPath(path);
        const form = currentSearchForm();
        // Parsing url parameters, and loading them
        const parameters = await form.parsePath(pathData);
        await form.loadViewUrlParams(parameters);
        urlParams.value = parameters;
        return parameters;
    };

    const initSearchForm = (viewUrl?: string) => {
        const currentForm = currentSearchForm();
        localStorage.viewUrl = viewUrl || currentForm.viewUrl;
        localStorage.setItem('lastSearch', currentForm.reserveInfo.type);
        currentForm.saveSearch();
    };

    const getFormModule = <T extends ItinerarySearchForm = ItinerarySearchForm>(
        type: ReserveType
    ): T => {
        switch (type) {
            case ReserveType.Train:
                return trainForm as any;
            case ReserveType.Bus:
                return busForm as any;
            case ReserveType.Taxi:
                return taxiForm as any;
            case ReserveType.Hotel:
                return hotelForm as any;
            case ReserveType.Ship:
                return shipForm as any;
            case ReserveType.Bundle:
                return bundleForm as any;
            case ReserveType.Flight:
            default:
                return flightForm as any;
        }
    };

    const setReserveType = (reserveType: ReserveType) => {
        if (
            !ReserveTypes.includes(reserveType) ||
            currentReserveType.value === reserveType
        ) {
            return;
        }
        currentReserveType.value = reserveType;
    };

    const currentSearchForm = <
        T extends ItinerarySearchForm = ItinerarySearchForm,
    >(): T => getFormModule<T>(reserveType.value);

    const shareItineraryInfo = (reserveType: ReserveType) => {
        const targetModule = getFormModule<TripSearchForm>(reserveType);
        const sourceModule = currentSearchForm();
        sourceModule.initPaxCounts.forEach(pax => {
            const targetPax = targetModule.initPaxCounts.find(
                p => p.code === pax.code
            );
            if (targetPax) {
                targetPax.count = pax.count;
            }
        });
        if (sourceModule instanceof TripSearchForm) {
            targetModule.departureDate = sourceModule.departureDate;
            targetModule.returnDate = sourceModule.returnDate;
        }
    };

    const getPassCount = (pkg: ItineraryPackage): PassengerCount => {
        let itiModule = getFormModule(pkg.reserveType);
        if (reserveType.value === ReserveType.Bundle) {
            itiModule = getFormModule(ReserveType.Bundle);
        }
        return {
            adultCount: itiModule.adultCount,
            childCount: itiModule.childCount,
            infantCount: itiModule.infantCount,
        };
    };

    /**
     * Indicates whether the alternative suggestion bar must be shown in view page, or not
     */
    const haveSuggestions = computed(
        () => !(reserveType.value === 'flight' && !flightForm.isDomestic)
    );

    return {
        needClearPackageSets,
        reserveType,
        _doNotUseCurrentReserveType: currentReserveType,
        firstReserveType,
        trainForm,
        busForm,
        taxiForm,
        flightForm,
        bundleForm,
        hotelForm,
        bundleHotelForm,
        bundleFlightForm,
        shipForm,
        urlParams,
        haveSuggestions,
        targetCode,
        setReserveType,
        currentSearchForm,
        shareItineraryInfo,
        getPassCount,
        getFormModule,
        initSearchForm,
        loadViewPath,
        verifyViewPath,
        setReserveTypeBaseOnPath,
    };
});

export const useSearchFormStoreIfInstalled = useStoreIfInstalled(
    storeKey,
    useSearchFormStore
);
